import type {FC} from 'react';
import {forwardRef, useEffect, useMemo, useState} from 'react';
import type {ReportType} from '../../../../../core/entities/reportAnalytics/schema';
import {convertStringToReportType} from '../../../../../core/factories/reports/convertStringToReportType';
import {
  type ReportAnalyzeStepResponse,
  useReportAnalyzeStep,
} from '../../../../../core/repositories/mypageEntryManagementsRepository';
import {useGetDepartmentsInSummary} from '../../../../../core/repositories/reportSummaryRepository';
import {showErrorSnackBar} from '../../../../../shared/components';
import {ReportTypeLabel} from '../../../../reports/components/Container/Presenter/ReportTypeLabel';
import {ReportAnalyzeModal} from '../Presenter/Progress/ReportAnalyzeModal';

type Props = {
  opened: boolean;
  onClose: () => void;
  companyID: number;
  companyName?: string;
  userEntryManagementID?: number;
  initialDepartment?: string;
  onSuccess: (result: ReportAnalyzeStepResponse) => void;
};

type SelectItemProps = {
  label: string;
  value: string;
  reportType: ReportType;
} & React.ComponentPropsWithoutRef<'div'>;

const DepartmentSelectItem = forwardRef<HTMLDivElement, SelectItemProps>(
  ({label, reportType, ...others}, ref) => (
    <div
      ref={ref}
      {...others}
      className="flex text-base items-center gap-8 p-8 cursor-pointer data-[hovered=true]:bg-background-1"
    >
      <ReportTypeLabel type={convertStringToReportType(reportType)} />
      <span className="flex-1">{label}</span>
    </div>
  ),
);

DepartmentSelectItem.displayName = 'DepartmentSelectItem';

export const ReportAnalyzeModalContainer: FC<Props> = ({
  opened,
  onClose,
  companyID,
  companyName,
  userEntryManagementID,
  initialDepartment,
  onSuccess,
}) => {
  const [selectedOption, setSelectedOption] = useState(initialDepartment ?? '');

  useEffect(() => {
    if (opened) {
      setSelectedOption(initialDepartment ?? '');
    }
  }, [opened, initialDepartment]);

  // 部門データ取得（selection）
  const {data: selectionDepts} = useGetDepartmentsInSummary({
    companyID,
    queryParams: {reportType: 'selection'},
    config: {enabled: opened && companyID > 0},
  });

  // 部門データ取得（intern）
  const {data: internDepts} = useGetDepartmentsInSummary({
    companyID,
    queryParams: {reportType: 'intern'},
    config: {enabled: opened && companyID > 0},
  });

  const reportAnalyzeMutation = useReportAnalyzeStep();

  // 選択肢のフォーマット
  const departmentOptions = useMemo(() => {
    const options: {
      value: string;
      label: string;
      reportType: ReportType;
    }[] = [];

    if (selectionDepts?.ok && selectionDepts.value.departments) {
      options.push(
        ...selectionDepts.value.departments.map((dept) => ({
          value: `${dept.departmentID}-selection`,
          label: dept.departmentName,
          reportType: 'selection' as const,
        })),
      );
    }

    if (internDepts?.ok && internDepts.value.departments) {
      options.push(
        ...internDepts.value.departments.map((dept) => ({
          value: `${dept.departmentID}-intern`,
          label: dept.departmentName,
          reportType: 'intern' as const,
        })),
      );
    }

    return options;
  }, [selectionDepts, internDepts]);

  const handleSubmit = async () => {
    if (!selectedOption) return;

    // "1-selection" -> departmentID: 1, reportType: "selection"
    const [deptIdStr, reportType] = selectedOption.split('-');
    const departmentID = Number(deptIdStr);

    try {
      const result = await reportAnalyzeMutation.mutateAsync({
        companyID,
        departmentID,
        reportType: reportType as ReportType,
        userEntryManagementID,
      });

      if (result.ok) {
        onSuccess(result.value);
        onClose();
        setSelectedOption('');
      } else {
        showErrorSnackBar({
          title: '生成失敗',
          message: 'ステップの生成に失敗しました',
        });
      }
    } catch (_error) {
      showErrorSnackBar({
        title: '生成失敗',
        message: 'ステップの生成に失敗しました',
      });
    }
  };

  const handleSelectChange = (value: string | null) => {
    setSelectedOption(value ?? '');
  };

  const handleClose = () => {
    onClose();
  };

  // leftSection用のバッジを作成
  const leftSectionBadge = selectedOption ? (
    <ReportTypeLabel
      type={convertStringToReportType(
        selectedOption.split('-')[1] as ReportType,
      )}
    />
  ) : null;

  return (
    <ReportAnalyzeModal
      opened={opened}
      onClose={handleClose}
      onSubmit={handleSubmit}
      departmentOptions={departmentOptions}
      selectedOption={selectedOption}
      onSelectChange={handleSelectChange}
      isLoading={reportAnalyzeMutation.isLoading}
      itemComponent={DepartmentSelectItem}
      badgeComponent={leftSectionBadge}
      companyName={companyName}
    />
  );
};
